
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import TopBar from 'TailwindcssComponents/TopBar';
import DataTable from 'TailwindcssComponents/Tables';
import axios from 'axios';
import FooterTailwind from 'TailwindcssComponents/Footer';
import DeleteDialog from 'components/DeleteDialogBox/deleteDialog';
import { ToastContainer, toast } from 'react-toastify';
import { UserDataContext } from 'context';
import TicketsTable from 'TailwindcssComponents/Tables/ticketsTable';

export default function ViewTicketsByEvents() {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const { userData, selectedOrganization } = useContext(UserDataContext);
    const userRole = userData?.isAdmin ? 'admin' : selectedOrganization?.name || 'user';
    const orgId = selectedOrganization?.organization?.id || null;
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [eventsData, setEventsData] = useState([]);
    const [openDeleteDialogBox, setOpenDeleteDialogBox] = useState();
    const [selectedEventId, seSelectedEventId] = useState(null);
    const [totalItemsCount, setTotalItemsCount] = useState(0);
    const [totalPagesCount, setTotalPagesCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [ticketsData, setTicketsData] = useState([]);
    const [registrationsData, setRegistrationsData] = useState([]);
    const [totalTicketsCount, setTotalTicketsCount] = useState(0);
    const [isArchived, setIsArchived] = useState(true);

    const fetchEvents = async () => {
        try {
            setIsLoading(true);
            const payLoad = {
                page: currentPage,
                limit: rowsPerPage,
                name: searchTerm,
                orgId,
                ...(isArchived ? { startDate: currentDate } : { startDate, endDate })
            };

            // Fetch all required data in parallel
            const [eventResponse, ticketResponse, registrationsResponse, paymentResponse, venueResponse] = await Promise.all([
                axios.get(`${apiUrl}event-service/event`, { params: payLoad }),
                axios.get(`${apiUrl}event-service/event-ticket`),
                axios.get(`${apiUrl}event-service/event-registration`),
                axios.get(`${apiUrl}event-service/event-payment`, { params: { status: 'paid' } }),
                axios.get(`${apiUrl}event-service/event-venue`),
            ]);

            // Validate responses
            if (!eventResponse.data?.success || !eventResponse.data?.data) throw new Error('Failed to fetch events');
            if (!ticketResponse.data?.success || !ticketResponse.data?.data) throw new Error('Failed to fetch tickets');
            if (!registrationsResponse.data?.success || !registrationsResponse.data?.data) throw new Error('Failed to fetch registrations');
            if (!paymentResponse.data?.success || !paymentResponse.data?.data) throw new Error('Failed to fetch payments');
            if (!venueResponse.data?.success || !venueResponse.data?.data) throw new Error('Failed to fetch venues');

            // Extract data
            const events = eventResponse?.data.data;
            const tickets = ticketResponse?.data.data;
            const registrations = registrationsResponse?.data.data;
            const payments = paymentResponse?.data.data;
            const venues = venueResponse?.data.data

            // Create maps for quick lookup
            const ticketsByEventId = tickets.reduce((acc, ticket) => {
                acc[ticket.eventId] = (acc[ticket.eventId] || 0) + 1;
                return acc;
            }, {});

            const registrationsByEventId = registrations.reduce((acc, registration) => {
                acc[registration.eventId] = (acc[registration.eventId] || 0) + 1;
                return acc;
            }, {});

            // Get tickets count via payments and registrations
            const ticketsByPayment = payments.reduce((acc, payment) => {
                const registrationIds = payment.registrationIds || [];
                registrationIds.forEach((regId) => {
                    const ticketCount = tickets.filter(ticket => ticket.registrationId === regId).length;
                    acc[payment.eventId] = (acc[payment.eventId] || 0) + ticketCount;
                });
                return acc;
            }, {});

            // Enhance events with counts
            const enrichedEvents = events.map(event => ({
                ...event,
                ticketCount: ticketsByEventId[event.id] || 0,
                registrationCount: registrationsByEventId[event.id] || 0,
                ticketCountByPayment: ticketsByPayment[event.id] || 0,
                venue: venues.find(venue => venue.id === event.venueId) || null
            }));
            // Update state
            setEventsData(enrichedEvents);
            setFilteredEvents(enrichedEvents);
            setTotalItemsCount(eventResponse.data.totalItems || 0);
            setTotalPagesCount(eventResponse.data.totalPages || 0);
            setTicketsData(tickets);
            setTotalTicketsCount(tickets.length);
            setRegistrationsData(registrations);
        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleActionClick = (id, action) => {
        if (action === 'view') {
            navigate(`/view-eventTickets/detailedView/${id}`);
        }
        else if (action === 'edit') {
            navigate(`/events/edit-event/${id}`);
        }
        else if (action === 'delete') {
            handleDelete(id);
        }
    };

    const handleDelete = async (id) => {
        seSelectedEventId(id);
        setOpenDeleteDialogBox(true);
    };

    const closeDeleteDialogBox = () => {
        setOpenDeleteDialogBox(false);
    };

    const handleDeleteConfirm = async () => {
        try {
            setIsLoading(true);
            const response = await axios.delete(`${apiUrl}event-service/event/${selectedEventId}`,
                {
                    headers: { 'Authorization': `Bearer ${bearerToken}` }
                });
            if (response?.data.success) {
                toast.warning(response?.data.message || 'Event has been deleted');
            }
            else throw new Error(response?.data.message || 'Unable to delete');
        } catch (error) {
            console.error(error.response?.data.message || error.message);
            toast.error(error.response?.data.message || error.message);
        } finally {
            fetchEvents();
            setOpenDeleteDialogBox(false);
            setIsLoading(false);
        }
    };

    const filterEvents = (term) => {
        if (!term) {
            setFilteredEvents(eventsData); // Reset to all data if search term is empty
            return;
        }
        const lowerCaseTerm = term.toLowerCase();
        const filtered = eventsData.filter((event) =>
            event.name.toLowerCase().includes(lowerCaseTerm) // Filter by name
        );
        setFilteredEvents(filtered);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        filterEvents(term);
    };

    useEffect(() => {
        fetchEvents();
    }, [currentPage, rowsPerPage, searchTerm, bearerToken, isArchived]);

    const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

    const previousDay = new Date();
    previousDay.setDate(previousDay.getDate() - 1);
    const endDate = previousDay.toISOString().split('T')[0]; // One day before current date

    const previousYear = new Date();
    previousYear.setFullYear(previousYear.getFullYear() - 1);
    const startDate = previousYear.toISOString().split('T')[0]; // One year before current date


    const columns = [
        {
            key: 'name', label: 'Event Name',
            render: (row) => (
                <div className="flex items-center gap-3">
                    <img
                        src={row.coverImage?.secure_url}
                        alt={`${row.name} cover`}
                        className="w-10 h-10 rounded-full object-cover"
                    />
                    <span>{row.name}</span>
                </div>
            ),
        },
        {
            key: 'totalTicketsCount',
            label: 'Total Tickets Registered',
            render: (row) => (ticketsData.filter((ticket) => ticket.eventId === row.id).length),
        },
        {
            key: 'totalTicketsConfirmed',
            label: 'Total Tickets Confirmed',
            render: (row) => (ticketsData.filter((ticket) => ticket.eventId === row.id && ticket.status === 'paid').length),
        },
        {
            key: 'totalUsersBooked',
            label: 'Total Users Booked',
            render: (row) => (registrationsData.filter((registration) => registration.eventId === row.id).length),
        },
    ];

    return (
        <TailwindLayout>
            <TopBar />
            <div className="p-12 bg-gray-100 min-h-screen">
                <TicketsTable
                    title={'Tickets'}
                    rows={filteredEvents}
                    columns={columns}
                    onActionClick={handleActionClick}
                    options={{ showHeader: true, showHeaderTwo: false, viewOption: true, editOption: true, addoption: false, statusFilterOption: false, dateFilterOption: false, typeFilterOption: false, searchOption: true, archiveFilterOption: true }}
                    totalCount={totalItemsCount}
                    pagesCount={totalPagesCount}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(limit) => setRowsPerPage(limit)}
                    onPageChange={(page) => setCurrentPage(page)}
                    onSearch={handleSearch}
                    searcItem={searchTerm}
                    role={userRole}
                    loaderStatus={isLoading}
                    isArchivedStatus={isArchived}
                    onToggle={(value) => setIsArchived(value)}
                />
            </div>
            <DeleteDialog
                open={openDeleteDialogBox}
                onClose={closeDeleteDialogBox}
                onDelete={handleDeleteConfirm}
                name={'event'}
            />
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <FooterTailwind />
        </TailwindLayout>
    )
}